import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  User,
  X,
  Send,
  Loader2,
  MapPin,
  Clock,
  Calendar,
  Bell,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  Maximize2,
  Minimize2,
  Coffee,
  Fuel,
  Navigation,
  Search,
  TrendingUp,
  Phone,
  Route,
  Plus,
  Shuffle,
  Square,
  ArrowUp,
  ArrowDown,
  ArrowRight,
} from 'lucide-react';
import RoutyMark from './RoutyMark';
import { useAppConfig } from '../../contexts/AppConfigContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useTenantData } from '../../contexts/TenantDataContext';
import { usePlan } from '../../contexts/PlanContext';
import { toIanaTimezone } from '../../utils/tenantTime';
import { config as appConfig } from '../../config/config';
import type { MessageParam } from '@anthropic-ai/sdk/resources/messages';
import { RoutyAgent } from '../../business/routy/RoutyAgent';
import {
  ROUTY_SETUP_FEATURES,
  fetchRouteEndpoints,
  fetchSmsProviderConnected,
  fetchTeamContext,
  type StartingLocation,
  type TeamContext,
} from '../../business/routy/routyContext';
import type {
  ConfirmRequest,
  ConfirmLine,
  ConfirmStopItem,
  ConfirmTimeChange,
  ConfirmChange,
  ConfirmOutlineRow,
} from '../../business/routy/routyTools';
import type {
  RoutyArtifact,
  RoutyPlaceCard,
  RoutySlotOptionsArtifact,
  RoutySlotOption,
  RoutyFillCandidatesArtifact,
  RoutyFillCandidate,
  RoutyDatePickerArtifact,
  RoutyAppointmentPickerArtifact,
  AppointmentPickChoice,
  RoutyMissingAddressArtifact,
  RoutyContactPickerArtifact,
  ContactPickChoice,
  RoutyOpenManualBookingArtifact,
  RoutyAvailabilityAuditArtifact,
  AvailabilityOpenSlot,
  RoutyChoicesArtifact,
  RoutyChoiceOption,
} from '../../business/routy/routyArtifacts';
import { RoutyAssistantClient, newIdempotencyKey } from '../../business/routy/RoutyAssistantClient';
import { recordRoutyUsage } from '../../business/routy/routyUsageTracker';
import type { RoutyProposedAction, RoutyChatMessage } from '../../business/routy/assistantContract';
import { parseLocalDateTime, formatDateMedium, formatDurationMinutes } from '../../utils/timeUtils';
import PlacesList from './PlacesList';
import StopCollectorCard, { type CollectedStop } from './StopCollectorCard';
import SlotOptionsCard from './SlotOptionsCard';
import BookingCollectorCard, { type CollectedBooking } from './BookingCollectorCard';
import FillCandidatesCard from './FillCandidatesCard';
import DatePickerCard from './DatePickerCard';
import AppointmentPickerCard from './AppointmentPickerCard';
import MissingAddressCard from './MissingAddressCard';
import ContactPickerCard from './ContactPickerCard';
import AvailabilityAuditCard from './AvailabilityAuditCard';
import SlotAuditCard from './SlotAuditCard';
import SupportCard from './SupportCard';
import BookedCard from './BookedCard';
import ManualBookingCard from './ManualBookingCard';
import DaySummaryCard from './DaySummaryCard';
import WeekReportCard from './WeekReportCard';
import AppointmentBriefCard from './AppointmentBriefCard';
import ChoicesCard from './ChoicesCard';
import SetupLinkCard from './SetupLinkCard';
import CreateBookingModal from '../booking/CreateBookingModal';

/**
 * A static, display-only record of a confirmation card the user typed a
 * follow-up over. Kept in the transcript so the proposal's details never
 * vanish mid-conversation — the agent re-presents a live card to act on it.
 */
interface ConfirmSnapshot {
  title: string;
  subtitle?: string;
  /** Loose icon type so both browser-mode ConfirmLine and backend RoutyConfirmLine fit. */
  lines: Array<{ icon?: string; text: string }>;
  stops?: ConfirmStopItem[];
  timeChange?: ConfirmTimeChange;
  changes?: ConfirmChange[];
  outline?: ConfirmOutlineRow[];
}

interface RoutyMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  artifacts?: RoutyArtifact[];
  proposedActions?: RoutyProposedAction[];
  /** When set, this message renders as a read-only confirmation-card record. */
  confirmSnapshot?: ConfirmSnapshot;
}

interface PersistedChat {
  messages: RoutyMessage[];
  history: MessageParam[];
  /**
   * Set when a confirmation card was still awaiting the user's decision at
   * save time. A refresh mid-confirmation used to drop the whole turn (the
   * card lives in transient state, not `messages`), so the user's question
   * survived but Routy's proposal vanished. Persisting a snapshot lets the
   * reload show the proposal as a read-only record instead.
   */
  pendingSnapshot?: ConfirmSnapshot;
}

const STORAGE_PREFIX = 'routy_chat_';
const MAX_PERSISTED_MESSAGES = 80;
// Cap the persisted Claude history too — it's much heavier than the display
// messages (tool_use inputs + full tool_result JSON per turn) and previously
// grew unbounded until localStorage.setItem hit quota and every save started
// silently failing (after which NOTHING persisted across refreshes anymore).
const MAX_PERSISTED_HISTORY = 120;

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

/** True for a user message that carries tool_result blocks (a tool round-trip). */
function isToolResultMessage(message: MessageParam): boolean {
  return (
    message.role === 'user' &&
    Array.isArray(message.content) &&
    message.content.some((block) => (block as { type?: string }).type === 'tool_result')
  );
}

/**
 * Return a copy of the history that is always valid to resume from:
 * - Drops a trailing assistant message whose tool_use blocks never received a
 *   tool_result (a turn interrupted mid-flight, e.g. by a refresh while a
 *   confirmation card was pending). Feeding that back to the API errors out.
 * - Trims from the front to MAX_PERSISTED_HISTORY entries, cutting only at a
 *   plain user message so a tool_result never loses its preceding tool_use.
 */
function sanitizeHistory(history: MessageParam[]): MessageParam[] {
  let out = [...history];
  while (out.length > 0) {
    const last = out[out.length - 1];
    const hasUnansweredToolUse =
      last.role === 'assistant' &&
      Array.isArray(last.content) &&
      last.content.some((block) => (block as { type?: string }).type === 'tool_use');
    if (!hasUnansweredToolUse) break;
    out.pop();
  }
  if (out.length > MAX_PERSISTED_HISTORY) {
    const from = out.length - MAX_PERSISTED_HISTORY;
    let start = from;
    while (start < out.length && !(out[start].role === 'user' && !isToolResultMessage(out[start]))) {
      start++;
    }
    // If no plain user message exists in the window (rare), keep everything —
    // cutting anywhere else could orphan a tool_result or start with a
    // non-user message, both of which the API rejects.
    if (start < out.length) out = out.slice(start);
  }
  return out;
}

function loadChat(userId: string): PersistedChat {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return { messages: [], history: [] };
    const parsed = JSON.parse(raw) as PersistedChat;
    return {
      messages: parsed.messages ?? [],
      // Sanitize on load too, so history persisted by older builds (which
      // could end mid-turn) never breaks the first message after a reload.
      history: sanitizeHistory(parsed.history ?? []),
      pendingSnapshot: parsed.pendingSnapshot,
    };
  } catch {
    return { messages: [], history: [] };
  }
}

function saveChat(userId: string, chat: PersistedChat): void {
  try {
    const payload: PersistedChat = {
      messages: chat.messages.slice(-MAX_PERSISTED_MESSAGES),
      history: sanitizeHistory(chat.history),
    };
    if (chat.pendingSnapshot) payload.pendingSnapshot = chat.pendingSnapshot;
    localStorage.setItem(storageKey(userId), JSON.stringify(payload));
  } catch {
    // Storage full or unavailable — non-fatal for a prototype.
  }
}

// ---------------------------------------------------------------------------
// Cross-tab coordination (QA 86bavqgu0). Routy is ONE conversation per user,
// so multiple open tabs must behave like one chat: every tab re-hydrates from
// localStorage when another tab writes (`storage` events only fire in OTHER
// tabs, so there is no self-echo), and a small turn lock stops a second tab
// from running its own turn on stale history mid-flight — which previously
// split related actions across contexts and let the last tab clobber the
// other's saved history.
// ---------------------------------------------------------------------------

/** A turn is considered abandoned (tab crashed/closed mid-turn) after this. */
const TURN_LOCK_TTL_MS = 45_000;
/** How often the sending tab refreshes its lock so it never looks abandoned. */
const TURN_LOCK_HEARTBEAT_MS = 15_000;

interface TurnLock {
  tabId: string;
  ts: number;
}

function turnLockKey(userId: string): string {
  return `${STORAGE_PREFIX}${userId}_turnlock`;
}

function newTabId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function readTurnLock(userId: string): TurnLock | null {
  try {
    const raw = localStorage.getItem(turnLockKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TurnLock;
    return parsed && typeof parsed.tabId === 'string' && typeof parsed.ts === 'number' ? parsed : null;
  } catch {
    return null;
  }
}

function writeTurnLock(userId: string, tabId: string): void {
  try {
    localStorage.setItem(turnLockKey(userId), JSON.stringify({ tabId, ts: Date.now() } satisfies TurnLock));
  } catch {
    // Storage unavailable — locking becomes best-effort, same as before.
  }
}

function releaseTurnLock(userId: string, tabId: string): void {
  try {
    const lock = readTurnLock(userId);
    // Never clear a lock some other tab now holds (e.g. ours expired mid-turn).
    if (lock && lock.tabId !== tabId) return;
    localStorage.removeItem(turnLockKey(userId));
  } catch {
    // Non-fatal.
  }
}

/** True when a DIFFERENT tab holds a fresh (non-expired) turn lock. */
function isTurnLockHeldByOther(userId: string, tabId: string): boolean {
  const lock = readTurnLock(userId);
  return !!lock && lock.tabId !== tabId && Date.now() - lock.ts < TURN_LOCK_TTL_MS;
}

/**
 * Model-visible note appended to the restored history when a reload interrupted
 * a pending confirmation, so Routy knows the proposal was never applied and can
 * re-present it (the read-only snapshot card alone is invisible to the model).
 */
function buildInterruptedConfirmNote(snapshot: ConfirmSnapshot): string {
  const stopLines = (snapshot.stops ?? []).map(
    (s) => `${s.title} — ${s.address} (${s.dateLabel} ${s.timeLabel}, ${s.durationMinutes} min)`,
  );
  const timeLine = snapshot.timeChange
    ? `from ${snapshot.timeChange.oldDateLabel} ${snapshot.timeChange.oldTimeLabel} to ${snapshot.timeChange.newDateLabel} ${snapshot.timeChange.newTimeLabel}`
    : '';
  const details = [
    snapshot.title,
    snapshot.subtitle,
    timeLine,
    ...(snapshot.changes ?? []).map((c) => `${c.label}: ${c.from} to ${c.to}`),
    ...snapshot.lines.map((l) => l.text),
    ...stopLines,
    ...(snapshot.outline ?? []).map((row) => (row.tag ? `${row.text} (${row.tag})` : row.text)),
  ]
    .filter(Boolean)
    .join('; ');
  return (
    'NOTE: the app was reloaded while a confirmation card was still awaiting the user\'s decision, ' +
    `so NOTHING was applied — the action did NOT run. The pending proposal was: ${details}. ` +
    'The user can still see that proposal as a read-only card. If they want to proceed, re-run the ' +
    'SAME action (call the appropriate tool again with the same details) so a fresh confirmation ' +
    'card appears; if they ask for something different, act on that instead. Never claim the ' +
    'interrupted action was completed.'
  );
}

/**
 * A message handed to Routy from elsewhere in the app (see `askRouty` in
 * RoutyContext). The id makes each hand-off distinct, so asking the same
 * question twice still sends twice.
 */
export interface RoutyPendingPrompt {
  id: string;
  text: string;
  /**
   * What the operator sees in the transcript. The `text` a hand-off sends is
   * written for the model — ids, tool names, the lot — and showing that raw is
   * a wall of machinery nobody asked for. Falls back to `text` when omitted.
   */
  display?: string;
}

interface RoutyPanelProps {
  isOpen: boolean;
  onClose: () => void;
  /** Sent as if the operator typed it, once the agent is ready. */
  pendingPrompt?: RoutyPendingPrompt | null;
  onPromptConsumed?: () => void;
}

type ExamplePrompt = { icon: React.ComponentType<{ className?: string }>; text: string };

// A deep pool of starter prompts so the empty state feels alive — a different
// handful surfaces every time the panel opens (and auto-rotates while idle),
// teaching the operator the breadth of what Routy can do.
const EXAMPLE_PROMPTS: ExamplePrompt[] = [
  { icon: Calendar, text: 'Schedule a 1pm interview tomorrow at Starbucks — do I have time?' },
  { icon: Clock, text: "I'm running 15 min behind — push everything back and notify them." },
  { icon: TrendingUp, text: 'How many appointments were booked today, and what were the lead sources?' },
  { icon: Coffee, text: 'Find a coffee shop near my 2pm stop.' },
  { icon: Calendar, text: 'What does my afternoon look like?' },
  { icon: RefreshCw, text: 'Move my 3pm to tomorrow morning and let the customer know.' },
  { icon: Search, text: 'Where are the gaps in my schedule this week?' },
  { icon: Plus, text: 'Add a 30-minute lunch break around noon.' },
  { icon: Bell, text: 'Cancel my last appointment and notify the customer.' },
  { icon: Navigation, text: "What's my first stop tomorrow, and when should I leave?" },
  { icon: Fuel, text: 'Find a gas station along my route this afternoon.' },
  { icon: Clock, text: 'Squeeze in a quick estimate before my 4pm if it fits.' },
  { icon: RefreshCw, text: 'Reschedule everything after 2pm to Thursday.' },
  { icon: Phone, text: "Text my next customer that I'm on the way." },
  { icon: Clock, text: 'How long until my next appointment?' },
  { icon: Route, text: "What's the drive time between my next two stops?" },
  { icon: TrendingUp, text: "Show me today's total appointments and revenue." },
  { icon: MapPin, text: 'Find a hardware store near my 11am.' },
  { icon: Clock, text: 'Do I have time for a 45-minute job at 1pm?' },
  { icon: RefreshCw, text: 'Clear my afternoon and reschedule it to tomorrow.' },
  { icon: Search, text: 'Which day this week is the most open?' },
  { icon: Calendar, text: "What's the closest open slot for a new customer?" },
  { icon: Bell, text: 'Push my 10am back 30 minutes and notify them.' },
  { icon: Coffee, text: 'Find lunch options between my noon and 2pm stops.' },
  { icon: Sparkles, text: 'Summarize what I have left for the rest of the day.' },
  { icon: MapPin, text: 'Add a stop at the supply store on my way home.' },
];

const SUGGESTION_COUNT = 4;

function pickSuggestions(count: number): ExamplePrompt[] {
  const pool = [...EXAMPLE_PROMPTS];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, count);
}

const LINE_ICONS: Record<NonNullable<ConfirmLine['icon']>, React.ComponentType<{ className?: string }>> = {
  map: MapPin,
  clock: Clock,
  calendar: Calendar,
  bell: Bell,
  user: User,
  repeat: RefreshCw,
};

const OUTLINE_TAG_CLASSES: Record<NonNullable<ConfirmOutlineRow['tag']>, string> = {
  New: 'bg-success-50 text-success-700',
  Edited: 'bg-accent-50 text-accent-700',
  Removed: 'bg-danger-50 text-danger-700',
};

const renderWithBold = (text: string): React.ReactNode => {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? (
      <strong key={i} className="font-semibold">
        {part.slice(2, -2)}
      </strong>
    ) : (
      part
    ),
  );
};

const RoutyPanel: React.FC<RoutyPanelProps> = ({ isOpen, onClose, pendingPrompt, onPromptConsumed }) => {
  const { config } = useAppConfig();
  const { companyTimezone: companyTzRaw } = useTenantData();
  const { canManageAllBookings, canEditCompanySettings, canEditBookingForms, canCreateBookingForms } = usePermissions();
  const { tier: planTier, hasFeature } = usePlan();
  const lockedFeatureKey = ROUTY_SETUP_FEATURES.filter((feature) => !hasFeature(feature)).join(',');

  const currentUserId = config?.currentUser?.id || '';
  const currentUserName =
    config?.currentUser?.name?.trim() || config?.currentUser?.userName || undefined;
  const companyTimezone = useMemo(() => toIanaTimezone(companyTzRaw), [companyTzRaw]);

  // When true, Routy runs server-side (the backend agent) instead of the
  // browser Anthropic loop. See routy-assistant-backend-spec.md.
  const backendMode = appConfig.routy.useBackend;

  const [messages, setMessages] = useState<RoutyMessage[]>([]);
  const [input, setInput] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [activity, setActivity] = useState<string | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<ConfirmRequest | null>(null);
  const [pendingAction, setPendingAction] = useState<RoutyProposedAction | null>(null);

  const [startLoc, setStartLoc] = useState<StartingLocation>({});
  const [endLoc, setEndLoc] = useState<StartingLocation>({});
  const [startLoaded, setStartLoaded] = useState(false);
  // Optional override: when the operator taps "Use my current location" (handy
  // when they're out driving), Routy plans from their live GPS position instead
  // of their saved home/office start. Null = fall back to the saved start.
  const [currentLocationOverride, setCurrentLocationOverride] = useState<StartingLocation | null>(null);
  const [geoStatus, setGeoStatus] = useState<'idle' | 'locating' | 'error'>('idle');
  const [smsProviderConnected, setSmsProviderConnected] = useState(false);
  const [smsLoaded, setSmsLoaded] = useState(false);
  // Team size context (active member count + names) so the system prompt can
  // tell a solo operator apart from a multi-member team ("our week" ambiguity).
  const [teamContext, setTeamContext] = useState<TeamContext | null>(null);
  const [hydrated, setHydrated] = useState(false);
  // True while ANOTHER tab holds the turn lock (Routy is mid-turn over there).
  // This tab shows a "working in another tab" state and blocks sends so the
  // shared conversation never forks (QA 86bavqgu0).
  const [remoteBusy, setRemoteBusy] = useState(false);
  const [suggestions, setSuggestions] = useState<ExamplePrompt[]>(() => pickSuggestions(SUGGESTION_COUNT));

  const reshuffleSuggestions = useCallback(() => setSuggestions(pickSuggestions(SUGGESTION_COUNT)), []);

  const confirmResolverRef = useRef<((approved: boolean) => void) | null>(null);
  const artifactsRef = useRef<RoutyArtifact[]>([]);
  const historyRef = useRef<MessageParam[]>([]);
  // Stable identity for this tab, for the cross-tab turn lock.
  const tabIdRef = useRef<string>(newTabId());
  // Ref mirror of isSending so the storage-event listener (a stable closure)
  // can tell whether it is safe to adopt state another tab just persisted.
  const isSendingRef = useRef(false);
  // Always-current agent handle for callbacks created before the agent memo
  // (requestConfirmation needs it to snapshot mid-turn history).
  const agentRef = useRef<RoutyAgent | null>(null);
  const conversationIdRef = useRef<string | null>(null);
  // Set when the user taps a "finish booking manually" hand-off card — opens
  // the full New Booking modal pre-filled with what Routy already collected
  // (QA 86bavq548: self-service continuation instead of a support hand-off).
  const [manualBooking, setManualBooking] = useState<RoutyOpenManualBookingArtifact | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  // Holds a message queued while a confirmation card was still pending, so it
  // can run as soon as the declined turn finishes settling. `agent` is the full
  // instruction Routy receives; `display` is what shows in the transcript (for
  // UI actions like Swap/Remove we send a precise instruction but show a short,
  // human label instead of dumping the raw prompt). `display: null` = no bubble.
  const redirectRef = useRef<{ agent: string; display: string | null } | null>(null);
  // Set when the user taps "Swap" on a stop: the next place they pick from the
  // options should REPLACE this stop (keeping the others), not add a new one.
  const swapContextRef = useRef<{ title: string; index: number } | null>(null);
  // Set when the user taps Stop (or interrupts with a new message) so the
  // settling turn renders a "Stopped" acknowledgement instead of "Done.".
  const stoppedByUserRef = useRef(false);

  // The browser path now routes Anthropic calls through the backend proxy
  // (AnthropicProxyController), so the key lives server-side and no client key is
  // required — Routy is "connected" whenever it's enabled. (The full backend
  // agent, backendMode, remains a separate future path.)
  const apiKeyMissing = false;

  // Browser mode: restore the prior conversation (messages + model context) from
  // localStorage so the chat keeps flowing across refreshes and open/close.
  useEffect(() => {
    if (backendMode || !currentUserId) return;
    const saved = loadChat(currentUserId);
    let history = saved.history;
    let restored = saved.messages;
    // A refresh interrupted a pending confirmation: show the proposal as a
    // read-only card (so the response isn't lost from the transcript) and tell
    // the model nothing was applied so it can re-present the action on request.
    if (saved.pendingSnapshot) {
      restored = [
        ...restored,
        { id: `snap-restored-${Date.now()}`, role: 'assistant', content: '', confirmSnapshot: saved.pendingSnapshot },
      ];
      history = [...history, { role: 'assistant', content: buildInterruptedConfirmNote(saved.pendingSnapshot) }];
      // The persist effect below re-saves without the snapshot flag right after
      // hydration, so the snapshot isn't re-applied on the next reload (it now
      // lives in `messages`/`history`).
    }
    historyRef.current = history;
    setMessages(restored);
    setHydrated(true);
  }, [backendMode, currentUserId]);

  // Backend mode: load the most recent server-side conversation for this user.
  useEffect(() => {
    if (!backendMode || !currentUserId) return;
    let cancelled = false;
    RoutyAssistantClient.listConversations()
      .then((list) => {
        if (cancelled || !list.length) return undefined;
        return RoutyAssistantClient.getConversation(list[0].id).then((conv) => {
          if (cancelled) return;
          conversationIdRef.current = conv.id;
          setMessages(conv.messages.map(backendMessageToRoutyMessage));
        });
      })
      .catch(() => {
        // No prior conversation / endpoint not ready — start fresh.
      })
      .finally(() => {
        if (!cancelled) setHydrated(true);
      });
    return () => {
      cancelled = true;
    };
  }, [backendMode, currentUserId]);

  // Browser mode: persist messages + model history to localStorage once hydrated.
  // Also persists while a confirmation card is awaiting a decision: the card
  // (and the whole in-flight turn) lives outside `messages`, so without a
  // snapshot a refresh at that moment silently dropped Routy's entire response
  // — the user's question survived but the proposal vanished (QA: "chat
  // responses are not preserved after refreshing").
  useEffect(() => {
    if (backendMode || !hydrated || !currentUserId) return;
    const pendingSnapshot: ConfirmSnapshot | undefined = pendingConfirm
      ? {
          title: pendingConfirm.title,
          subtitle: pendingConfirm.subtitle,
          lines: pendingConfirm.lines,
          stops: pendingConfirm.stops,
          timeChange: pendingConfirm.timeChange,
          changes: pendingConfirm.changes,
          outline: pendingConfirm.outline,
        }
      : undefined;
    saveChat(currentUserId, { messages, history: historyRef.current, pendingSnapshot });
  }, [backendMode, messages, hydrated, currentUserId, pendingConfirm]);

  useEffect(() => {
    isSendingRef.current = isSending;
  }, [isSending]);

  // Browser mode: live cross-tab sync (QA 86bavqgu0). `storage` events fire
  // only in OTHER tabs, so when this fires the change came from a sibling tab:
  // adopt its persisted transcript + model history (so this tab's next turn
  // continues the SAME conversation instead of forking from a stale copy), and
  // track its turn lock so this tab shows a busy state instead of racing it.
  useEffect(() => {
    if (backendMode || !currentUserId || !hydrated) return;
    const chatKey = storageKey(currentUserId);
    const lockKey = turnLockKey(currentUserId);
    const onStorage = (e: StorageEvent) => {
      if (e.key === lockKey) {
        setRemoteBusy(isTurnLockHeldByOther(currentUserId, tabIdRef.current));
        return;
      }
      if (e.key !== chatKey) return;
      // Never clobber a turn running HERE (shouldn't happen — the lock keeps
      // turns mutually exclusive — but a stale/expired lock makes it possible).
      if (isSendingRef.current) return;
      const saved = loadChat(currentUserId);
      // The sibling tab is still live, so a pendingSnapshot in its save is an
      // ACTIVE card over there, not an interrupted one — adopt the transcript
      // verbatim and let that tab settle its own card (its next save carries
      // the outcome).
      historyRef.current = saved.history;
      agentRef.current?.setHistory(saved.history);
      setMessages(saved.messages);
    };
    window.addEventListener('storage', onStorage);
    setRemoteBusy(isTurnLockHeldByOther(currentUserId, tabIdRef.current));
    return () => window.removeEventListener('storage', onStorage);
  }, [backendMode, currentUserId, hydrated]);

  // A crashed/closed tab never clears its lock — poll while blocked so this
  // tab frees itself once the abandoned lock passes its TTL (no storage event
  // fires for mere expiry).
  useEffect(() => {
    if (backendMode || !currentUserId || !remoteBusy) return;
    const id = setInterval(() => {
      setRemoteBusy(isTurnLockHeldByOther(currentUserId, tabIdRef.current));
    }, 5000);
    return () => clearInterval(id);
  }, [backendMode, currentUserId, remoteBusy]);

  // Closing/navigating away mid-turn should free the lock immediately instead
  // of making sibling tabs wait out the TTL. releaseTurnLock is a no-op when
  // this tab doesn't hold it.
  useEffect(() => {
    if (backendMode || !currentUserId) return;
    const release = () => releaseTurnLock(currentUserId, tabIdRef.current);
    window.addEventListener('pagehide', release);
    return () => window.removeEventListener('pagehide', release);
  }, [backendMode, currentUserId]);

  // Browser mode only: resolve the operator's starting/home location so place
  // search and routing can bias around it. In backend mode the server resolves
  // this. Failure is non-fatal (Routy still works).
  useEffect(() => {
    // Only resolve the agent's route/team context once the panel is actually
    // opened. The panel stays mounted while closed (see `if (!isOpen) return null`
    // below), so without this guard these fetches (getTeamMembers, companySettings,
    // companyLocation) fired on every page load for a closed panel.
    if (!isOpen) return;
    if (backendMode) {
      setStartLoaded(true);
      return;
    }
    if (apiKeyMissing || !currentUserId) {
      setStartLoaded(true);
      return;
    }
    let cancelled = false;
    fetchRouteEndpoints(currentUserId)
      .then(({ start, end }) => {
        if (!cancelled) {
          setStartLoc(start);
          setEndLoc(end);
        }
      })
      .finally(() => {
        if (!cancelled) setStartLoaded(true);
      });
    // Team size for the system prompt - best-effort (null = unknown), so it
    // never gates the agent from starting.
    fetchTeamContext().then((team) => {
      if (!cancelled) setTeamContext(team);
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, backendMode, apiKeyMissing, currentUserId]);

  // Browser mode only: check whether a texting provider (Twilio/OpenPhone) is
  // connected so tools never offer an SMS notify toggle that would silently
  // fail to send - same guard Create Booking / Edit Event / On The Way apply
  // via useSmsProviderStatus. In backend mode the server checks this itself.
  useEffect(() => {
    // Same as the route/team pre-warm above: only probe SMS provider status once
    // the panel is opened, not on every page load while closed.
    if (!isOpen) return;
    if (backendMode) {
      setSmsLoaded(true);
      return;
    }
    if (apiKeyMissing || !currentUserId) {
      setSmsLoaded(true);
      return;
    }
    let cancelled = false;
    fetchSmsProviderConnected()
      .then((connected) => {
        if (!cancelled) setSmsProviderConnected(connected);
      })
      .finally(() => {
        if (!cancelled) setSmsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, backendMode, apiKeyMissing, currentUserId]);

  const requestConfirmation = useCallback(
    (request: ConfirmRequest) =>
      new Promise<boolean>((resolve) => {
        // Sync the model history now (normally only synced when the turn
        // finishes) so the save that snapshots this pending card also carries
        // the user's question from the in-flight turn. sanitizeHistory trims
        // the turn's unanswered tool_use tail on save, keeping it resumable.
        if (agentRef.current) historyRef.current = agentRef.current.getHistory();
        setPendingConfirm(request);
        confirmResolverRef.current = resolve;
      }),
    [],
  );

  const resolveConfirmation = useCallback((approved: boolean) => {
    setPendingConfirm(null);
    confirmResolverRef.current?.(approved);
    confirmResolverRef.current = null;
  }, []);

  // The start location the agent plans from: the live GPS override if the
  // operator opted into it, otherwise their saved home/office start.
  const effectiveStartLoc = currentLocationOverride ?? startLoc;

  // One agent per operator/timezone/start-location. Built after the starting
  // location resolves so its first turn already has full context. Rebuilds
  // (preserving history) when the operator toggles their current-location
  // override so subsequent turns plan from the right starting point.
  const agent = useMemo(() => {
    if (backendMode || apiKeyMissing || !currentUserId || !startLoaded || !smsLoaded || !hydrated) return null;
    return new RoutyAgent({
      model: appConfig.anthropic.model,
      env: {
        currentUserId,
        currentUserName,
        companyTimezone,
        startingAddress: effectiveStartLoc.address,
        startingCoordinates: effectiveStartLoc.coordinates,
        // Drive-leg copy + system prompt say "your current location" while
        // the live-GPS override is on, instead of "your starting location".
        startingLabel: currentLocationOverride ? 'your current location' : undefined,
        endingAddress: endLoc.address,
        endingCoordinates: endLoc.coordinates,
        canManageAllBookings,
        smsProviderConnected,
        activeTeamMemberCount: teamContext?.activeMemberCount,
        teamMemberNames: teamContext?.memberNames,
        canEditCompanySettings,
        canEditBookingForms,
        canCreateBookingForms,
        planTier,
        lockedFeatures: lockedFeatureKey ? lockedFeatureKey.split(',') : [],
        canMentionBilling: true,
      },
      initialHistory: historyRef.current,
      onActivity: (label) => setActivity(label),
      requestConfirmation,
      onArtifact: (artifact) => artifactsRef.current.push(artifact),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendMode, apiKeyMissing, currentUserId, currentUserName, companyTimezone, startLoaded, effectiveStartLoc, endLoc, smsLoaded, smsProviderConnected, hydrated, canManageAllBookings, teamContext, canEditCompanySettings, canEditBookingForms, canCreateBookingForms, planTier, lockedFeatureKey]);

  useEffect(() => {
    agentRef.current = agent;
  }, [agent]);

  // Ask the browser for the operator's live GPS position and use it as the
  // route start. Optional — declining/failing just keeps the saved start.
  const requestCurrentLocation = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setGeoStatus('error');
      return;
    }
    setGeoStatus('locating');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCurrentLocationOverride({
          address: 'Your current location',
          coordinates: { lat: pos.coords.latitude, lng: pos.coords.longitude },
        });
        setGeoStatus('idle');
      },
      () => setGeoStatus('error'),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  }, []);

  const clearCurrentLocation = useCallback(() => {
    setCurrentLocationOverride(null);
    setGeoStatus('idle');
  }, []);

  // `isOpen` must be a dependency: the component stays mounted while closed
  // (it just renders null), so reopening rebuilds the scroll container at
  // scrollTop 0 without any of the other deps changing — the restored chat
  // then showed from the TOP instead of the latest message (QA 86bavp935).
  // Routy's answers are long — an audit runs several paragraphs plus a card —
  // so pinning the newest reply's TOP to the top of the viewport lands the user
  // at the first line instead of the tail end of a card they then have to
  // scroll back up through. Only done once per reply, when it first appears:
  // re-anchoring on every token would fight the user as they read.
  //
  // The position that anchor put us at is remembered, because the commits that
  // follow (the turn settling, a card finishing layout) have to reason about
  // what WILL be on screen once the smooth scroll lands, not where it happens
  // to be mid-animation.
  const anchoredReplyRef = useRef<{ id: string; top: number } | null>(null);
  /** Replies whose cards have already been brought into view, so reading is never interrupted twice. */
  const anchoredCardsRef = useRef<string | null>(null);
  const messagesRef = useRef(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Reopening a restored transcript must still land at the bottom (QA
  // 86bavp935), so treat whatever reply is already on screen as read. Declared
  // first so it seeds before the scroll effect runs on the same commit.
  useEffect(() => {
    if (!isOpen) return;
    const lastReply = [...messagesRef.current].reverse().find((m) => m.role === 'assistant');
    anchoredReplyRef.current = lastReply ? { id: lastReply.id, top: 0 } : null;
    anchoredCardsRef.current = lastReply?.id ?? null;
  }, [isOpen]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!isOpen || !el) return;

    // Only the NEWEST message can be anchored: once the user (or a card tap)
    // sends something, the newest thing is theirs and the transcript follows
    // the bottom again.
    const last = messages[messages.length - 1];
    const reply = last && last.role === 'assistant' && !last.confirmSnapshot ? last : null;

    if (reply && anchoredReplyRef.current?.id !== reply.id) {
      const node = el.querySelector<HTMLElement>(`[data-routy-reply="${CSS.escape(reply.id)}"]`);
      if (node) {
        // Bias up by the container's padding so the avatar isn't flush against
        // the header edge. Clamped by the browser when the reply is the last
        // thing in a short transcript.
        const top = Math.max(0, node.offsetTop - el.offsetTop - 12);
        anchoredReplyRef.current = { id: reply.id, top };
        el.scrollTo({ top, behavior: 'smooth' });
        return;
      }
    }

    // The reply is placed and no new turn is running. Pinning to the bottom
    // here is what used to cancel the anchor mid-scroll and drop the operator
    // at the TAIL of a tall card — time options whose header and best pick had
    // scrolled off the top. So instead: leave the scroll alone, except to bring
    // the cards into view once when they run past the fold, since they are the
    // part that has to be acted on.
    if (reply && !activity) {
      if (anchoredCardsRef.current !== reply.id) {
        const cards = el.querySelector<HTMLElement>(`[data-routy-cards="${CSS.escape(reply.id)}"]`);
        if (cards) {
          anchoredCardsRef.current = reply.id;
          const cardsTop = cards.offsetTop - el.offsetTop;
          const viewTop = anchoredReplyRef.current?.id === reply.id ? anchoredReplyRef.current.top : el.scrollTop;
          if (cardsTop + cards.offsetHeight > viewTop + el.clientHeight) {
            const top = Math.max(0, cardsTop - 12);
            anchoredReplyRef.current = { id: reply.id, top };
            el.scrollTo({ top, behavior: 'smooth' });
          }
        }
      }
      return;
    }

    // Everything else — the user's own message, a running activity line, a
    // confirmation card — should keep the newest thing in view.
    el.scrollTop = el.scrollHeight;
  }, [isOpen, messages, activity, pendingConfirm, pendingAction]);

  // Auto-grow the composer as the user types (up to max-h-28 / 7rem, after
  // which it scrolls). Reset to auto first so it shrinks back when text is
  // deleted or cleared after sending.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 112)}px`;
  }, [input]);

  // Fresh set of starter ideas only when the panel opens (a fresh interface),
  // so the examples don't cycle/shift while the user is reading them.
  useEffect(() => {
    if (isOpen) reshuffleSuggestions();
  }, [isOpen, reshuffleSuggestions]);

  // Always-current reference to `runTurn` (defined below), so the
  // redirect/finally hooks above it in the call graph never invoke a
  // stale/closed-over copy from an earlier render (e.g. from before `agent`
  // was ready).
  const runTurnRef = useRef<(text: string, options?: { silent?: boolean; initialActivity?: string; displayText?: string | null }) => Promise<void>>(async () => {});

  // Runs a redirect message queued while a confirmation card was pending, as
  // soon as the declined turn finishes settling.
  const runQueuedRedirect = useCallback(() => {
    if (!redirectRef.current) return;
    const next = redirectRef.current;
    redirectRef.current = null;
    // Friendlier status than "Thinking..." so a redirected re-plan reads as
    // "I'm updating the plan", not "did my message send?".
    void runTurnRef.current(next.agent, {
      initialActivity: 'Reviewing your change…',
      displayText: next.display,
    });
  }, []);

  // Backend mode: one agent turn handled server-side.
  const sendTextBackend = useCallback(
    async (trimmed: string, options?: { silent?: boolean; displayText?: string | null }) => {
      // Same contract as the browser path: undefined shows the message itself,
      // a string shows that instead, null shows nothing.
      const bubble = options?.displayText === undefined ? trimmed : options.displayText;
      if (!options?.silent && bubble) {
        setMessages((prev) => [...prev, { id: `u-${Date.now()}`, role: 'user', content: bubble }]);
      }
      setInput('');
      setIsSending(true);
      setActivity('Thinking...');
      const turnStartedAt = Date.now();

      try {
        const res = await RoutyAssistantClient.sendMessage(trimmed, conversationIdRef.current ?? undefined);
        conversationIdRef.current = res.conversationId;
        setMessages((prev) => [
          ...prev,
          {
            id: `a-${Date.now()}`,
            role: 'assistant',
            content: res.reply || 'Done.',
            artifacts: res.artifacts?.length ? res.artifacts : undefined,
            proposedActions: res.proposedActions?.length ? res.proposedActions : undefined,
          },
        ]);
        setPendingAction(res.proposedActions?.length ? res.proposedActions[0] : null);
        recordRoutyUsage({ surface: 'chat', outcome: 'ok', latencyMs: Date.now() - turnStartedAt });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Something went wrong talking to Routy.';
        setMessages((prev) => [...prev, { id: `e-${Date.now()}`, role: 'assistant', content: `Sorry — ${message}` }]);
        recordRoutyUsage({ surface: 'chat', outcome: 'error', latencyMs: Date.now() - turnStartedAt });
      } finally {
        setIsSending(false);
        setActivity(null);
        runQueuedRedirect();
      }
    },
    [runQueuedRedirect],
  );

  // Core turn runner - does NOT guard on `isSending`. Only call this when it's
  // already known to be safe (from `sendText`'s guarded entry point, or from
  // the queued-redirect finally hooks once a prior turn has settled).
  const runTurn = useCallback(
    async (trimmed: string, options?: { silent?: boolean; initialActivity?: string; displayText?: string | null }) => {
      if (backendMode) {
        await sendTextBackend(trimmed, options);
        return;
      }

      if (!agent) return;
      // Cross-tab turn lock: only one tab may run a turn on the shared
      // conversation at a time. If a sibling tab is mid-turn, surface the busy
      // state instead of racing it on stale history (QA 86bavqgu0).
      if (currentUserId && isTurnLockHeldByOther(currentUserId, tabIdRef.current)) {
        setRemoteBusy(true);
        return;
      }
      let lockHeartbeat: ReturnType<typeof setInterval> | undefined;
      if (currentUserId) {
        writeTurnLock(currentUserId, tabIdRef.current);
        // Refresh the lock while the turn runs (turns can outlive the TTL,
        // e.g. while a confirmation card waits on the user).
        lockHeartbeat = setInterval(
          () => writeTurnLock(currentUserId, tabIdRef.current),
          TURN_LOCK_HEARTBEAT_MS,
        );
      }
      // `displayText` lets a UI action (Swap/Remove) send Routy a precise
      // instruction while showing a short label — or nothing (null) — instead
      // of the raw prompt. Undefined falls back to the message text itself.
      const bubble = options?.displayText === undefined ? trimmed : options.displayText;
      if (!options?.silent && bubble) {
        setMessages((prev) => {
          // Guard against a queued redirect and a direct send racing to show the
          // same message twice — skip if the last bubble is an identical user one.
          const last = prev[prev.length - 1];
          if (last && last.role === 'user' && last.content === bubble) return prev;
          return [...prev, { id: `u-${Date.now()}`, role: 'user', content: bubble }];
        });
      }
      setInput('');
      setIsSending(true);
      setActivity(options?.initialActivity ?? 'Thinking...');
      artifactsRef.current = [];
      const turnStartedAt = Date.now();

      try {
        const replyPromise = agent.sendMessage(trimmed);
        // sendMessage pushed the user message synchronously; point historyRef
        // at the live history now so any mid-turn save (e.g. a pending-confirm
        // snapshot, or a refresh) already includes this turn's question.
        historyRef.current = agent.getHistory();
        const reply = await replyPromise;
        const wasStopped = stoppedByUserRef.current;
        stoppedByUserRef.current = false;
        const artifacts = artifactsRef.current.length ? [...artifactsRef.current] : undefined;
        historyRef.current = agent.getHistory();
        // A silent turn (empty reply, no artifacts) is a redirect being
        // discarded — don't render a hollow "Done." bubble; the queued
        // follow-up turn produces the real next message.
        const hasContent = !!reply?.trim() || !!artifacts?.length;
        if (hasContent) {
          setMessages((prev) => [
            ...prev,
            {
              id: `a-${Date.now()}`,
              role: 'assistant',
              content: reply || (wasStopped ? "Stopped — here's what I had so far." : 'Done.'),
              artifacts,
            },
          ]);
          // Record usage for the admin Routy Usage view (fire-and-forget).
          recordRoutyUsage({ surface: 'chat', outcome: 'ok', latencyMs: Date.now() - turnStartedAt });
        } else if (wasStopped && !redirectRef.current) {
          // A bare Stop (no interrupting message queued): acknowledge so the
          // spinner vanishing doesn't read as a hang. When a new instruction
          // IS queued, stay silent — that message drives the next turn.
          setMessages((prev) => [
            ...prev,
            { id: `a-${Date.now()}`, role: 'assistant', content: 'Stopped — no changes made. What would you like me to do instead?' },
          ]);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Something went wrong talking to Routy.';
        historyRef.current = agent.getHistory();
        setMessages((prev) => [
          ...prev,
          { id: `e-${Date.now()}`, role: 'assistant', content: `Sorry — ${message}` },
        ]);
        recordRoutyUsage({ surface: 'chat', outcome: 'error', latencyMs: Date.now() - turnStartedAt });
      } finally {
        if (lockHeartbeat !== undefined) clearInterval(lockHeartbeat);
        if (currentUserId) releaseTurnLock(currentUserId, tabIdRef.current);
        setIsSending(false);
        setActivity(null);
        if (confirmResolverRef.current) resolveConfirmation(false);
        runQueuedRedirect();
      }
    },
    [backendMode, sendTextBackend, agent, currentUserId, resolveConfirmation, runQueuedRedirect],
  );

  useEffect(() => {
    runTurnRef.current = runTurn;
  }, [runTurn]);

  const sendText = useCallback(
    (text: string, options?: { silent?: boolean; displayText?: string | null }) => {
      const trimmed = text.trim();
      if (!trimmed || isSending) return;
      void runTurn(trimmed, options);
    },
    [isSending, runTurn],
  );

  // A question handed over from elsewhere in the app (e.g. the slot picker's
  // "Don't see the time you need?" flow). It runs as a normal user message so
  // both the question and Routy's answer read naturally in the transcript.
  // Held until the agent is actually built — the panel can open well before
  // the route/SMS/team context resolves, and a send before then is dropped.
  const consumedPromptRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isOpen || !pendingPrompt || isSending) return;
    if (consumedPromptRef.current === pendingPrompt.id) return;
    if (!backendMode && !agent) return;
    consumedPromptRef.current = pendingPrompt.id;
    // Omitted entirely when there's no display line, so `runTurn` falls back to
    // showing the message itself rather than suppressing the bubble.
    sendText(pendingPrompt.text, pendingPrompt.display ? { displayText: pendingPrompt.display } : undefined);
    onPromptConsumed?.();
  }, [isOpen, pendingPrompt, isSending, backendMode, agent, sendText, onPromptConsumed]);

  // Backend mode: approve/decline a proposed write (or pick an alternative).
  const handleExecuteAction = useCallback(
    async (action: RoutyProposedAction, approved: boolean, alternativeId?: string) => {
      setPendingAction(null);
      if (!approved && !conversationIdRef.current) return;
      setIsSending(true);
      setActivity(approved ? 'Working on it...' : null);
      try {
        const res = await RoutyAssistantClient.execute({
          conversationId: conversationIdRef.current as string,
          actionId: action.id,
          approved,
          idempotencyKey: newIdempotencyKey(),
          alternativeId,
        });
        const follow = res.followUp;
        if (follow) {
          conversationIdRef.current = follow.conversationId;
          setMessages((prev) => [
            ...prev,
            {
              id: `a-${Date.now()}`,
              role: 'assistant',
              content: follow.reply || res.resultSummary || 'Done.',
              artifacts: follow.artifacts?.length ? follow.artifacts : undefined,
              proposedActions: follow.proposedActions?.length ? follow.proposedActions : undefined,
            },
          ]);
          setPendingAction(follow.proposedActions?.length ? follow.proposedActions[0] : null);
        } else {
          setMessages((prev) => [
            ...prev,
            {
              id: `a-${Date.now()}`,
              role: 'assistant',
              content: res.resultSummary || (approved ? 'Done.' : "No problem — I haven't made any changes."),
            },
          ]);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Something went wrong applying that.';
        setMessages((prev) => [...prev, { id: `e-${Date.now()}`, role: 'assistant', content: `Sorry — ${message}` }]);
      } finally {
        setIsSending(false);
        setActivity(null);
        runQueuedRedirect();
      }
    },
    [runQueuedRedirect],
  );

  // Public entry point for anything the user types. If a confirmation card is
  // still pending, typing a new instruction ("actually, add one more stop")
  // declines the pending card first (no changes made) and queues the new
  // message to run the moment that decline settles, instead of forcing the
  // user to tap Confirm/Cancel before they can redirect the conversation.
  const submitUserText = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      if (pendingConfirm || pendingAction) {
        // Freeze the pending card into the transcript as a read-only record so
        // the proposal's details stay visible while the follow-up is answered
        // (the agent re-presents a live card when the user is ready to act).
        const snapshot: ConfirmSnapshot | null = pendingConfirm
          ? {
              title: pendingConfirm.title,
              subtitle: pendingConfirm.subtitle,
              lines: pendingConfirm.lines,
              stops: pendingConfirm.stops,
              timeChange: pendingConfirm.timeChange,
              changes: pendingConfirm.changes,
              outline: pendingConfirm.outline,
            }
          : pendingAction
            ? { title: pendingAction.title, lines: pendingAction.lines }
            : null;
        if (snapshot) {
          setMessages((prev) => [
            ...prev,
            { id: `snap-${Date.now()}`, role: 'assistant', content: '', confirmSnapshot: snapshot },
          ]);
        }
        redirectRef.current = { agent: trimmed, display: trimmed };
        setInput('');
        // Tell the agent this decline is a redirect so it ends the current turn
        // silently (no premature "No problem — no changes" message) and lets the
        // queued follow-up drive the visible re-plan.
        agent?.markRedirecting();
        if (pendingConfirm) resolveConfirmation(false);
        if (pendingAction) handleExecuteAction(pendingAction, false);
        return;
      }
      // Mid-turn interrupt (browser mode): Routy is still thinking with no
      // decision card up. Queue the new message and stop the running turn —
      // the queued message runs the moment the turn settles, so the user can
      // correct or add context without waiting (mirrors common LLM chat UX).
      if (!backendMode && isSending) {
        redirectRef.current = { agent: trimmed, display: trimmed };
        stoppedByUserRef.current = true;
        setInput('');
        agent?.stop();
        return;
      }
      sendText(trimmed);
    },
    [pendingConfirm, pendingAction, resolveConfirmation, handleExecuteAction, sendText, agent, backendMode, isSending],
  );

  // Stop button: interrupt the running turn without sending anything new. If a
  // confirmation card is somehow pending, declining it lets the blocked turn
  // wind down (stop takes precedence over the decline inside the agent).
  const handleStop = useCallback(() => {
    if (!agent || !isSending) return;
    stoppedByUserRef.current = true;
    agent.stop();
    if (confirmResolverRef.current) resolveConfirmation(false);
  }, [agent, isSending, resolveConfirmation]);

  // Fire a UI action (Swap/Remove on the plan card): Routy receives the full
  // precise `agentText`, but the transcript shows only the short `displayText`
  // (or nothing) instead of the raw instruction. Always runs over a pending
  // confirm card, so it goes through the same silent-redirect path.
  const submitStopAction = useCallback(
    (agentText: string, displayText: string | null) => {
      redirectRef.current = { agent: agentText, display: displayText };
      agent?.markRedirecting();
      if (pendingConfirm) resolveConfirmation(false);
      else if (pendingAction) handleExecuteAction(pendingAction, false);
      else {
        // No pending card (defensive): run it directly, still hiding the raw text.
        redirectRef.current = null;
        void runTurn(agentText, { displayText });
      }
    },
    [pendingConfirm, pendingAction, resolveConfirmation, handleExecuteAction, agent, runTurn],
  );

  const handleNewChat = useCallback(() => {
    setMessages([]);
    setPendingAction(null);
    if (backendMode) {
      conversationIdRef.current = null;
      RoutyAssistantClient.newConversation()
        .then((res) => {
          conversationIdRef.current = res.conversationId;
        })
        .catch(() => {
          // Backend will lazily create a conversation on the next message.
        });
      return;
    }
    agent?.reset();
    historyRef.current = [];
    artifactsRef.current = [];
    if (currentUserId) saveChat(currentUserId, { messages: [], history: [] });
  }, [backendMode, agent, currentUserId]);

  const handleSchedulePlace = useCallback(
    (place: RoutyPlaceCard, durationMinutes: number) => {
      // If the user is swapping a stop, the picked place REPLACES that stop and
      // the rest of the plan is kept — not a brand-new standalone stop.
      const swap = swapContextRef.current;
      if (swap) {
        swapContextRef.current = null;
        sendText(
          `Replace my "${swap.title}" stop with ${place.name}. Use these EXACT values for it (do NOT ` +
            `re-geocode): address="${place.address}", latitude=${place.lat}, longitude=${place.lng}, ` +
            `duration=${durationMinutes} min. Keep all my other stops exactly as they are (same places, ` +
            `via their address+latitude+longitude) and re-optimize the whole day into one plan.`,
          { silent: true },
        );
        return;
      }
      const when = place.date && place.startTime ? ` at ${place.startTime} on ${place.date}` : '';
      // Pass the place's EXACT coordinates + full address through so add_stop
      // uses them verbatim and never re-geocodes the business name (which would
      // land on the wrong branch of a chain). See add_stop's latitude/longitude.
      sendText(
        `Yes — schedule a ${durationMinutes}-minute stop titled "${place.name}"${when}. ` +
          `Call add_stop with these EXACT values (do NOT look them up or re-geocode): ` +
          `address="${place.address}", latitude=${place.lat}, longitude=${place.lng}.`,
        { silent: true },
      );
    },
    [sendText],
  );

  const handleSubmitStops = useCallback(
    (stops: CollectedStop[], context: { startDate?: string; endDate?: string }) => {
      if (!stops.length) return;
      const lines = stops
        .map((s, i) => `${i + 1}. ${s.title} — ${s.address} (${s.durationMinutes} min)`)
        .join('\n');
      const dateLine = context.startDate
        ? ` for ${context.startDate}${context.endDate ? ` through ${context.endDate}` : ''}`
        : '';
      sendText(
        `Here are the stops I want to add${dateLine}. Please fit them into my schedule in the most ` +
          `route-efficient order:\n${lines}`,
        { silent: true },
      );
    },
    [sendText],
  );

  const handleSelectSlot = useCallback(
    (artifact: RoutySlotOptionsArtifact, option: RoutySlotOption) => {
      if (artifact.kind === 'reschedule') {
        sendText(
          `Yes — reschedule ${artifact.appointmentLabel || 'that appointment'} (appointmentId ` +
            `${artifact.appointmentId}) to ${option.date} at ${option.startTime}. Notify the customer.`,
          { silent: true },
        );
      } else {
        sendText(
          `Yes — book the ${option.date} ${option.startTime} slot (slotSuggestionId ${option.slotId}) ` +
            `for requestId ${artifact.requestId}. Send the customer their confirmation.`,
          { silent: true },
        );
      }
    },
    [sendText],
  );

  const handleSubmitBooking = useCallback(
    (booking: CollectedBooking) => {
      const contactLine =
        booking.mode === 'existing' && booking.contactId
          ? `existing contactId ${booking.contactId}`
          : `a new contact: ${booking.firstName} ${booking.lastName}, phone ${booking.phone}, email ${booking.email}`;
      const coords = booking.lat != null && booking.lng != null ? ` (lat ${booking.lat}, lng ${booking.lng})` : '';
      const timeLine = booking.preferredTimeRange ? ` in the ${booking.preferredTimeRange}` : '';
      sendText(
        `Here are the booking details — please find available times:\n` +
          `- Service: ${booking.bookingFormName} (bookingFormId ${booking.bookingFormId})\n` +
          `- Customer: ${contactLine}\n` +
          `- Address: ${booking.address}${coords}\n` +
          `- Preferred date: ${booking.preferredDate}${timeLine}`,
        { silent: true },
      );
    },
    [sendText],
  );

  const handleSelectFill = useCallback(
    (artifact: RoutyFillCandidatesArtifact, candidate: RoutyFillCandidate) => {
      sendText(
        `Yes — move ${candidate.contactName}'s appointment (appointmentId ${candidate.appointmentId}) ` +
          `into my gap: reschedule it to ${artifact.date} at ${candidate.proposedStartTime} ` +
          `(${candidate.durationMinutes} min). Notify the customer.`,
        { silent: true },
      );
    },
    [sendText],
  );

  const handlePickDate = useCallback(
    (artifact: RoutyDatePickerArtifact, date: string) => {
      const parsed = parseLocalDateTime(date);
      const friendly = parsed ? formatDateMedium(parsed) : date;
      const purpose = artifact.purpose ? ` for ${artifact.purpose}` : '';
      sendText(`Use ${friendly} (${date})${purpose}.`, { silent: true });
    },
    [sendText],
  );

  const handlePickAppointment = useCallback(
    (artifact: RoutyAppointmentPickerArtifact, candidate: AppointmentPickChoice) => {
      const parsed = parseLocalDateTime(candidate.date);
      const when = parsed ? formatDateMedium(parsed) : candidate.date;
      const addr = candidate.address ? `, address ${candidate.address}` : '';
      const purpose = artifact.purpose ? ` Continue with ${artifact.purpose}.` : '';
      sendText(
        `Yes — that's the one: ${candidate.title} on ${when} (appointmentId ${candidate.appointmentId})${addr}.${purpose}`,
        { silent: true },
      );
    },
    [sendText],
  );

  const handleNoneAppointment = useCallback(
    (_artifact: RoutyAppointmentPickerArtifact) => {
      sendText(`None of those are the right one — let me clarify which job/appointment I mean.`, { silent: true });
    },
    [sendText],
  );

  const handleSubmitMissingAddress = useCallback(
    (artifact: RoutyMissingAddressArtifact, address: string) => {
      const purpose = artifact.purpose ? ` Continue with ${artifact.purpose}.` : ' Continue where you left off.';
      sendText(`${artifact.subject}'s address is: ${address}.${purpose}`, { silent: true });
    },
    [sendText],
  );

  const handlePickContact = useCallback(
    (artifact: RoutyContactPickerArtifact, candidate: ContactPickChoice) => {
      const addr = candidate.address ? `, address ${candidate.address}` : '';
      const purpose = artifact.purpose ? ` Continue with ${artifact.purpose}.` : '';
      sendText(
        `Yes — that's the right contact: ${candidate.name} (contactId ${candidate.contactId})${addr}.${purpose}`,
        { silent: true },
      );
    },
    [sendText],
  );

  const handleNoneContact = useCallback(
    (_artifact: RoutyContactPickerArtifact) => {
      sendText(`None of those are the right contact — let me clarify who I mean.`, { silent: true });
    },
    [sendText],
  );

  const handleSubmitChoices = useCallback(
    (artifact: RoutyChoicesArtifact, picked: RoutyChoiceOption[]) => {
      const answer = picked.map((option) => option.label).join(', ');
      const purpose = artifact.purpose ? ` Continue with ${artifact.purpose}.` : '';
      sendText(`My answer to "${artifact.prompt}": ${answer}.${purpose}`, { displayText: answer });
    },
    [sendText],
  );

  const handleRemoveAuditEvents = useCallback(
    (artifact: RoutyAvailabilityAuditArtifact, eventIds: string[]) => {
      if (eventIds.length === 0) return;
      const assignee = artifact.assignedUserId ? `, assignedUserId ${artifact.assignedUserId}` : '';
      // When the selection is exactly "everything without an address" — the
      // card's default, and what the button means most of the time — hand over
      // a flag instead of a list of UUIDs. Ids have to survive being written
      // into prose and read back out, and a single dropped one silently leaves
      // the day blocked.
      const unlocatedIds = artifact.blockers
        .filter((b) => b.removable && b.missingAddress && b.id)
        .map((b) => b.id as string);
      const isEveryUnlocated =
        unlocatedIds.length === eventIds.length && unlocatedIds.every((id) => eventIds.includes(id));

      sendText(
        isEveryUnlocated
          ? `Clear every synced calendar event without an address off ${artifact.date} so they stop blocking ` +
              `my availability — call remove_calendar_events with date ${artifact.date}, ` +
              `removeUnlocated true${assignee}. All ${eventIds.length} of them. Once they're gone, help me ` +
              `finish booking that day.`
          : `Remove these ${eventIds.length} synced calendar events from ${artifact.date} so they stop ` +
              `blocking my availability — call remove_calendar_events with date ${artifact.date}, ` +
              `expectedCount ${eventIds.length}${assignee}, and ALL of these eventIds: ` +
              `[${eventIds.join(', ')}]. Do not drop any. Once they're gone, help me finish booking that day.`,
        { silent: true },
      );
    },
    [sendText],
  );

  const handleAuditSlot = useCallback(
    (artifact: RoutyAvailabilityAuditArtifact, slot: AvailabilityOpenSlot) => {
      const assignee = artifact.assignedUserId ? `, assignedUserId ${artifact.assignedUserId}` : '';
      // The window's exact 24h bounds ride along so the tool measures the same
      // window the operator tapped, rather than re-deriving one from prose.
      sendText(
        `Audit the ${slot.startLabel}–${slot.endLabel} window on ${artifact.date} — call audit_slot with ` +
          `date ${artifact.date}, startTime ${slot.startTime}, endTime ${slot.endTime}${assignee}` +
          `${artifact.bookingFormId ? `, bookingFormId ${artifact.bookingFormId}` : ''}. ` +
          'Include the service being booked so the visit length and buffers are right, and pass the ' +
          "customer's address if you have it so the drive legs are measured to the real job.",
        // Reads as the question they asked, without the ids and tool name.
        { displayText: `Audit the ${slot.startLabel} – ${slot.endLabel} window on ${artifact.dateLabel}.` },
      );
    },
    [sendText],
  );

  // The day was open all along and simply not requested, so the fix is a fresh
  // slot search on that same request — not the operator re-entering a booking
  // Routemize already has.
  const handleRerunSlots = useCallback(
    (artifact: RoutyAvailabilityAuditArtifact) => {
      const requestId = artifact.slotPreference?.requestId;
      if (!requestId) return;
      sendText(
        `Re-run the times for ${artifact.date} on request ${requestId} — call create_booking_request with ` +
          `existingRequestId ${requestId} and preferredDate ${artifact.date}. Everything else (customer, ` +
          'service, address) comes off the request, so do not ask me for any of it.',
        { displayText: `Re-run the times for ${artifact.dateLabel}.` },
      );
    },
    [sendText],
  );

  const handleKeepAuditEvents = useCallback(
    (artifact: RoutyAvailabilityAuditArtifact) => {
      sendText(
        `Keep all of those — don't remove anything from ${artifact.date}. What are my other options for ` +
          `getting this booked?`,
        { silent: true },
      );
    },
    [sendText],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitUserText(input);
  };

  if (!isOpen) return null;

  const notReady = backendMode ? !currentUserId : apiKeyMissing || !agent;
  const hasPending = !!pendingConfirm || !!pendingAction;
  // The input stays enabled while Routy works: over a pending card, typing
  // declines-and-redirects; mid-generation (browser mode), typing interrupts
  // the turn and the new message runs right after (submitUserText). Only
  // backend mode still locks during a turn (no server-side stop yet). A turn
  // running in ANOTHER tab also locks the input — the conversation is shared,
  // so this tab must not fork it mid-turn.
  const inputDisabled = notReady || remoteBusy || (backendMode && isSending && !hasPending);
  // Browser mode shows a Stop control while a turn is running with no
  // decision card up (the card has its own Cancel).
  const canStop = !backendMode && isSending && !hasPending;

  // Below `sm`, a docked corner box (or even the "expanded" 780px window)
  // doesn't work on a phone-sized viewport — both states become a full-screen
  // sheet instead, which is the standard mobile chat-widget pattern. Desktop
  // (`sm:` and up) keeps the floating-box position as before, sitting at
  // `bottom-24` so it clears the launcher button in the corner (`bottom-5`,
  // see RoutyContext.tsx). The docked box is `h-auto` with a ~600px floor so
  // it GROWS upward with the conversation instead of locking at one size; the
  // `max-h` reserves ~13rem of space (launcher gap + a comfortable top margin)
  // so on shorter laptop viewports the window stops growing upward instead of
  // nearly reaching the top bar (the `min()` in min-h keeps the floor from
  // ever beating that cap on short viewports, since min-height wins over
  // max-height in CSS).
  // NOTE: `calc()` in Tailwind arbitrary values needs whitespace around the
  // operator, written as underscores (`calc(100vh_-_13rem)`). Without them the
  // declaration is invalid CSS and silently dropped — which is exactly what
  // made the panel ignore its max-height and launch too tall/high before.
  //
  // The `inset-0` class (kept for the mobile full-screen sheet) also matches a
  // global impersonation rule in index.css that force-sets `top` to the banner
  // height on `.fixed.inset-0` overlays. On the desktop docked panel that
  // hijacks the bottom-right anchoring and pins it under the banner, so
  // `sm:!top-auto` (important) opts the desktop layout back out. Mobile keeps
  // the offset so the full-screen sheet still clears the banner.
  const containerClass = isExpanded
    ? 'fixed inset-0 z-[9998] w-full h-full sm:m-auto sm:w-[780px] sm:max-w-[calc(100vw_-_2rem)] sm:h-[84vh] sm:max-h-[calc(100vh_-_3rem)] flex flex-col bg-surface rounded-none sm:rounded-xl shadow-2xl shadow-ink-950/10 border-0 sm:border sm:border-ink-200 overflow-hidden animate-routy-in'
    : 'fixed inset-0 sm:inset-auto sm:!top-auto z-[9998] w-full h-full sm:bottom-24 sm:right-5 sm:w-[400px] sm:max-w-[calc(100vw_-_2rem)] sm:h-auto sm:min-h-[min(600px,calc(100vh_-_13rem))] sm:max-h-[calc(100vh_-_13rem)] flex flex-col bg-surface rounded-none sm:rounded-xl shadow-2xl shadow-ink-950/10 border-0 sm:border sm:border-ink-200 overflow-hidden animate-routy-in';

  return (
    <>
      {/* Dimmed backdrop behind the expanded desktop window so the panel
          reads as a focused overlay instead of meshing with the page behind
          it. Clicking it shrinks back to the docked corner box. Hidden below
          `sm` (mobile is already a full-screen sheet). */}
      {isExpanded && (
        <div
          data-routy-ui="true"
          aria-hidden="true"
          onClick={() => setIsExpanded(false)}
          className="hidden sm:block fixed inset-0 z-[9997] bg-ink-950/40 backdrop-blur-[2px] animate-routy-backdrop-in"
        />
      )}
    <div className={containerClass} data-routy-ui="true">
      {/* Header. Extra top padding on mobile clears notches/status bars since
          the sheet runs edge-to-edge there instead of floating in a corner. */}
      <div className="flex items-center justify-between px-4 pt-[max(0.75rem,env(safe-area-inset-top))] pb-3 sm:py-3 bg-surface border-b border-ink-200 flex-shrink-0">
        <div className="flex items-center gap-2.5 min-w-0">
          <RoutyMark className="w-8 h-8" textClassName="text-xs" busy={isSending} />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="text-sm font-semibold leading-tight text-ink-900">Routy</p>
              <span className="inline-flex items-center rounded-full bg-accent-100 text-accent-700 text-2xs font-bold uppercase tracking-wide px-1.5 py-0.5 leading-none">
                Beta
              </span>
            </div>
            <p className="text-2xs text-ink-400 leading-tight">Scheduling assistant</p>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {messages.length > 0 && (
            <button
              onClick={handleNewChat}
              disabled={isSending || remoteBusy}
              aria-label="New chat"
              title="New chat"
              className="h-9 sm:h-8 px-2.5 rounded-md text-ink-500 hover:text-ink-900 hover:bg-ink-100 flex items-center gap-1.5 text-xs font-medium transition-colors disabled:opacity-50"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span className="hidden xs:inline">New</span>
            </button>
          )}
          {/* Expand/shrink only makes sense on desktop — both states are
              already a full-screen sheet below `sm`. */}
          <button
            onClick={() => setIsExpanded((v) => !v)}
            aria-label={isExpanded ? 'Shrink window' : 'Expand window'}
            title={isExpanded ? 'Shrink' : 'Expand'}
            className="hidden sm:flex w-8 h-8 rounded-md text-ink-500 hover:text-ink-900 hover:bg-ink-100 items-center justify-center transition-colors"
          >
            {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
          <button
            onClick={onClose}
            aria-label="Close Routy"
            className="w-9 h-9 sm:w-8 sm:h-8 rounded-md text-ink-500 hover:text-ink-900 hover:bg-ink-100 flex items-center justify-center transition-colors"
          >
            <X className="w-4.5 h-4.5" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-surface">
        {apiKeyMissing ? (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900">
            <p className="font-semibold mb-1">Routy isn't connected yet</p>
            <p className="text-amber-800/90">
              Set <code className="px-1 py-0.5 bg-amber-100 rounded">VITE_ANTHROPIC_API_KEY</code> and
              restart the dev server to enable the assistant.
            </p>
          </div>
        ) : messages.length === 0 ? (
          <div className="pt-8">
            <div className="flex flex-col items-center text-center gap-3 mb-6">
              <RoutyMark className="w-10 h-10" textClassName="text-sm" />
              <div>
                <p className="text-sm font-semibold text-ink-900">
                  Hey{currentUserName ? `, ${currentUserName.split(' ')[0]}` : ''}
                </p>
                <p className="text-xs text-ink-500 max-w-[280px] mt-1">
                  I can see your whole schedule, find places along your route, move things around, and
                  keep customers in the loop. Try one of these:
                </p>
              </div>
            </div>
            <div className="space-y-2">
              {suggestions.map((suggestion, i) => {
                const Icon = suggestion.icon;
                return (
                  <button
                    key={suggestion.text}
                    onClick={() => sendText(suggestion.text)}
                    disabled={inputDisabled}
                    style={{ animationDelay: `${i * 60}ms` }}
                    className="routy-suggest group w-full flex items-start gap-2.5 text-left text-sm px-3.5 py-3 rounded-lg bg-surface border border-ink-200 text-ink-700 hover:border-ink-300 hover:bg-ink-50 transition-colors duration-150 disabled:opacity-60"
                  >
                    <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-ink-100 text-ink-500 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-150">
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                    <span className="leading-snug">{suggestion.text}</span>
                  </button>
                );
              })}
              <button
                onClick={reshuffleSuggestions}
                disabled={inputDisabled}
                className="mx-auto mt-1 flex items-center gap-1.5 text-2xs font-medium text-ink-400 hover:text-ink-700 transition-colors disabled:opacity-60"
              >
                <Shuffle className="h-3 w-3" />
                Show me other ideas
              </button>
            </div>
          </div>
        ) : (
          messages.map((message) => (
            <React.Fragment key={message.id}>
              {message.confirmSnapshot ? (
                <ConfirmActionCard
                  title={message.confirmSnapshot.title}
                  subtitle={message.confirmSnapshot.subtitle}
                  lines={message.confirmSnapshot.lines}
                  stops={message.confirmSnapshot.stops}
                  timeChange={message.confirmSnapshot.timeChange}
                  changes={message.confirmSnapshot.changes}
                  outline={message.confirmSnapshot.outline}
                  confirmLabel=""
                  readOnly
                  onConfirm={() => {}}
                  onCancel={() => {}}
                />
              ) : (
                <MessageBubble message={message} />
              )}
              {/* Grouped so the scroll effect can bring a reply's cards into
                  view as one block — a tall one (eight time options) otherwise
                  lands with its header and best option above the fold. */}
              {!!message.artifacts?.length && (
              <div data-routy-cards={message.id} className="space-y-4">
              {message.artifacts.map((artifact, ai) => {
                if (artifact.type === 'places') {
                  return (
                    <PlacesList
                      key={`${message.id}-${ai}-places`}
                      cards={artifact.cards}
                      durationMinutes={artifact.durationMinutes}
                      disabled={isSending || !!pendingConfirm}
                      onSchedule={handleSchedulePlace}
                    />
                  );
                }
                if (artifact.type === 'stop_collector') {
                  return (
                    <StopCollectorCard
                      key={`${message.id}-${ai}-stops`}
                      artifact={artifact}
                      disabled={isSending || !!pendingConfirm}
                      onSubmit={handleSubmitStops}
                    />
                  );
                }
                if (artifact.type === 'slot_options') {
                  return (
                    <SlotOptionsCard
                      key={`${message.id}-${ai}-slots`}
                      artifact={artifact}
                      disabled={isSending || !!pendingConfirm}
                      onSelect={handleSelectSlot}
                    />
                  );
                }
                if (artifact.type === 'booking_collector') {
                  return (
                    <BookingCollectorCard
                      key={`${message.id}-${ai}-booking`}
                      artifact={artifact}
                      disabled={isSending || !!pendingConfirm}
                      onSubmit={handleSubmitBooking}
                    />
                  );
                }
                if (artifact.type === 'fill_candidates') {
                  return (
                    <FillCandidatesCard
                      key={`${message.id}-${ai}-fill`}
                      artifact={artifact}
                      disabled={isSending || !!pendingConfirm}
                      onSelect={handleSelectFill}
                    />
                  );
                }
                if (artifact.type === 'date_picker') {
                  return (
                    <DatePickerCard
                      key={`${message.id}-${ai}-datepicker`}
                      artifact={artifact}
                      disabled={isSending || !!pendingConfirm}
                      onPick={handlePickDate}
                    />
                  );
                }
                if (artifact.type === 'appointment_picker') {
                  return (
                    <AppointmentPickerCard
                      key={`${message.id}-${ai}-apptpicker`}
                      artifact={artifact}
                      disabled={isSending || !!pendingConfirm}
                      onPick={handlePickAppointment}
                      onNone={handleNoneAppointment}
                    />
                  );
                }
                if (artifact.type === 'missing_address') {
                  return (
                    <MissingAddressCard
                      key={`${message.id}-${ai}-missingaddr`}
                      artifact={artifact}
                      disabled={isSending || !!pendingConfirm}
                      onSubmit={handleSubmitMissingAddress}
                    />
                  );
                }
                if (artifact.type === 'contact_picker') {
                  return (
                    <ContactPickerCard
                      key={`${message.id}-${ai}-contactpicker`}
                      artifact={artifact}
                      disabled={isSending || !!pendingConfirm}
                      onPick={handlePickContact}
                      onNone={handleNoneContact}
                    />
                  );
                }
                if (artifact.type === 'availability_audit') {
                  return (
                    <AvailabilityAuditCard
                      key={`${message.id}-${ai}-availabilityaudit`}
                      artifact={artifact}
                      disabled={isSending || !!pendingConfirm}
                      onRemove={handleRemoveAuditEvents}
                      onKeep={handleKeepAuditEvents}
                      onAuditSlot={handleAuditSlot}
                      onRerunSlots={handleRerunSlots}
                    />
                  );
                }
                if (artifact.type === 'slot_audit') {
                  return <SlotAuditCard key={`${message.id}-${ai}-slotaudit`} artifact={artifact} />;
                }
                if (artifact.type === 'booking_confirmed') {
                  return <BookedCard key={`${message.id}-${ai}-booked`} artifact={artifact} />;
                }
                if (artifact.type === 'open_manual_booking') {
                  return (
                    <ManualBookingCard
                      key={`${message.id}-${ai}-manualbooking`}
                      artifact={artifact}
                      onOpen={setManualBooking}
                    />
                  );
                }
                if (artifact.type === 'day_summary') {
                  return <DaySummaryCard key={`${message.id}-${ai}-daysummary`} artifact={artifact} />;
                }
                if (artifact.type === 'week_report') {
                  return <WeekReportCard key={`${message.id}-${ai}-weekreport`} artifact={artifact} />;
                }
                if (artifact.type === 'appointment_brief') {
                  return <AppointmentBriefCard key={`${message.id}-${ai}-apptbrief`} artifact={artifact} />;
                }
                if (artifact.type === 'support') {
                  // Close the panel when the Support Chat opens — the Intercom
                  // messenger occupies the same bottom-right corner and would
                  // otherwise sit hidden behind the Routy window.
                  return <SupportCard key={`${message.id}-${ai}-support`} artifact={artifact} onOpenedSupportChat={onClose} />;
                }
                if (artifact.type === 'choices') {
                  return (
                    <ChoicesCard
                      key={`${message.id}-${ai}-choices`}
                      artifact={artifact}
                      disabled={isSending || !!pendingConfirm}
                      onSubmit={handleSubmitChoices}
                    />
                  );
                }
                if (artifact.type === 'setup_link') {
                  return (
                    <SetupLinkCard
                      key={`${message.id}-${ai}-setuplink`}
                      artifact={artifact}
                      onOpened={() => setIsExpanded(false)}
                    />
                  );
                }
                return null;
              })}
              </div>
              )}
            </React.Fragment>
          ))
        )}

        {isSending && activity && (
          <div className="flex items-center gap-2.5 text-xs text-ink-500">
            <RoutyMark className="w-7 h-7" busy />
            <span className="routy-thinking">{activity}</span>
          </div>
        )}

        {/* A sibling tab is running a turn on this same conversation. Show the
            shared-session state here; the transcript live-updates the moment
            that tab saves (storage-event sync above). */}
        {remoteBusy && !isSending && (
          <div className="flex items-center gap-2.5 text-xs text-ink-500">
            <RoutyMark className="w-7 h-7" busy />
            <span className="routy-thinking">Routy is working on this in another tab…</span>
          </div>
        )}

        {pendingConfirm && (
          <ConfirmActionCard
            title={pendingConfirm.title}
            subtitle={pendingConfirm.subtitle}
            lines={pendingConfirm.lines}
            stops={pendingConfirm.stops}
            timeChange={pendingConfirm.timeChange}
            changes={pendingConfirm.changes}
            outline={pendingConfirm.outline}
            confirmLabel={pendingConfirm.confirmLabel}
            tone={pendingConfirm.tone}
            notify={pendingConfirm.notify}
            onConfirm={(notify) => {
              pendingConfirm.notify?.onDecision?.(notify);
              resolveConfirmation(true);
            }}
            onSwapStop={
              pendingConfirm.stops && pendingConfirm.stops.length > 0
                ? (stop, index) => {
                    // Remember which stop we're replacing so the option the user
                    // picks substitutes in (rather than adding a new stop).
                    swapContextRef.current = { title: stop.title, index };
                    submitStopAction(
                      `Show me the 3 best alternative options for stop ${index + 1} ("${stop.title}") ` +
                        `ranked by how little they add to my route — present them as selectable cards with ` +
                        `drive times, and do NOT auto-pick or re-plan yet. I'll choose one, and when I do, ` +
                        `replace only that stop and keep the others exactly as they are.`,
                      `Show me other options for ${stop.title}`,
                    );
                  }
                : undefined
            }
            onRemoveStop={
              pendingConfirm.stops && pendingConfirm.stops.length > 1
                ? (stop, index) =>
                    submitStopAction(
                      `Remove stop ${index + 1} ("${stop.title}") — keep all my other stops exactly as they ` +
                        `are (same places), drop just that one, and re-optimize the remaining route.`,
                      `Remove ${stop.title}`,
                    )
                : undefined
            }
            onCancel={() => resolveConfirmation(false)}
          />
        )}

        {pendingAction && (
          <ConfirmActionCard
            title={pendingAction.title}
            lines={pendingAction.lines}
            confirmLabel={pendingAction.confirmLabel}
            tone={pendingAction.tone}
            alternatives={pendingAction.alternatives}
            onAlternative={(altId) => handleExecuteAction(pendingAction, true, altId)}
            onConfirm={() => handleExecuteAction(pendingAction, true)}
            onCancel={() => handleExecuteAction(pendingAction, false)}
          />
        )}
      </div>

      {/* Input. Bottom safe-area padding clears the home indicator on notched
          phones now that this runs edge-to-edge on mobile instead of floating. */}
      <form onSubmit={handleSubmit} className="flex-shrink-0 border-t border-ink-200 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] bg-surface">
        {/* Current-location control. Optional — lets someone out on the road
            plan from where they actually are instead of their saved start.
            Only shown in the browser prototype (backend resolves start itself). */}
        {!backendMode && (
          <div className="mb-2 flex items-center gap-2">
            {currentLocationOverride ? (
              <span className="inline-flex items-center gap-1.5 max-w-full rounded-full bg-blue-50 border border-blue-200 pl-2.5 pr-1.5 py-1 text-xs font-medium text-blue-700">
                <Navigation className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">Starting from your current location</span>
                <button
                  type="button"
                  onClick={clearCurrentLocation}
                  aria-label="Use saved start location instead"
                  className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center hover:bg-blue-100 transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ) : (
              <button
                type="button"
                onClick={requestCurrentLocation}
                disabled={geoStatus === 'locating'}
                className="inline-flex items-center gap-1.5 rounded-full bg-ink-50 border border-ink-200 px-2.5 py-1 text-xs font-medium text-ink-600 hover:text-ink-900 hover:border-ink-300 active:scale-95 transition-all disabled:opacity-60"
              >
                {geoStatus === 'locating' ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Navigation className="h-3.5 w-3.5" />
                )}
                {geoStatus === 'locating' ? 'Locating…' : 'Use my current location'}
              </button>
            )}
            {geoStatus === 'error' && !currentLocationOverride && (
              <span className="text-2xs text-ink-400">Couldn't get your location</span>
            )}
          </div>
        )}
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submitUserText(input);
              }
            }}
            rows={1}
            disabled={inputDisabled}
            placeholder={
              remoteBusy && !isSending
                ? 'Routy is working in another tab...'
                : pendingConfirm || pendingAction
                  ? 'Confirm above, or tell me what to change...'
                  : canStop
                    ? 'Routy is working — type to interrupt...'
                    : 'Ask Routy anything...'
            }
            className="flex-1 resize-none max-h-28 overflow-y-auto px-3 py-2.5 bg-ink-50 border border-ink-200 rounded-lg text-sm placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-colors disabled:opacity-60"
          />
          {canStop && !input.trim() ? (
            <button
              type="button"
              onClick={handleStop}
              aria-label="Stop generating"
              title="Stop"
              className="w-9 h-9 rounded-lg bg-ink-900 text-white flex items-center justify-center hover:bg-ink-700 active:scale-95 transition-all flex-shrink-0"
            >
              <Square className="w-3.5 h-3.5 fill-current" />
            </button>
          ) : (
            <button
              type="submit"
              disabled={!input.trim() || inputDisabled}
              aria-label="Send"
              className="w-9 h-9 rounded-lg bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
            >
              {isSending && !canStop ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          )}
        </div>
      </form>

      {/* Manual booking hand-off (QA 86bavq548): the full New Booking modal,
          pre-filled with everything Routy collected and continuing the same
          saved request, so a failed automated booking finishes self-service.
          NOT skipToSlotSelection — the operator walks the normal steps
          (including the Questions step, which handles conditional logic). */}
      {manualBooking && (
        <CreateBookingModal
          isOpen
          onClose={() => setManualBooking(null)}
          onSave={() => setManualBooking(null)}
          existingRequestId={manualBooking.requestId}
          prefilledEventTypeId={manualBooking.bookingFormId}
          prefilledDate={manualBooking.preferredDate}
          prefilledCustomer={
            manualBooking.prefill
              ? {
                  firstName: manualBooking.prefill.firstName,
                  lastName: manualBooking.prefill.lastName,
                  email: manualBooking.prefill.email,
                  phone: manualBooking.prefill.phone,
                  address: manualBooking.prefill.address,
                  contactId: manualBooking.prefill.contactId,
                  contactAddressId: manualBooking.prefill.contactAddressId,
                }
              : undefined
          }
        />
      )}

      <style>{`
        @keyframes routy-in {
          from { opacity: 0; transform: translateY(8px) scale(0.99); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-routy-in { animation: routy-in 0.16s ease-out forwards; }

        @keyframes routy-suggest-in {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .routy-suggest { animation: routy-suggest-in 0.3s ease-out backwards; }

        @keyframes routy-msg-in {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .routy-msg-in { animation: routy-msg-in 0.2s ease-out backwards; }

        @keyframes routy-thinking-pulse {
          0%, 100% { opacity: 0.55; }
          50% { opacity: 1; }
        }
        .routy-thinking { animation: routy-thinking-pulse 1.4s ease-in-out infinite; }

        @media (prefers-reduced-motion: reduce) {
          .routy-suggest, .routy-msg-in, .routy-thinking, .animate-routy-in, .animate-routy-backdrop-in { animation: none; }
        }

        @keyframes routy-backdrop-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-routy-backdrop-in { animation: routy-backdrop-in 0.16s ease-out forwards; }

        /* Google Places autocomplete dropdown must float above the panel. */
        .pac-container { z-index: 10001 !important; }
      `}</style>
    </div>
    </>
  );
};

const MessageBubble: React.FC<{ message: RoutyMessage }> = ({ message }) => {
  const isBot = message.role === 'assistant';
  if (isBot) {
    return (
      <div data-routy-reply={message.id} className="routy-msg-in flex gap-2.5 items-start">
        <RoutyMark className="w-7 h-7 mt-0.5" textClassName="text-[10px]" />
        <div className="min-w-0 flex-1 pt-0.5 text-sm leading-relaxed text-ink-800 whitespace-pre-wrap break-words">
          {renderWithBold(message.content)}
        </div>
      </div>
    );
  }
  return (
    <div className="routy-msg-in flex justify-end">
      <div className="min-w-0 max-w-[82%] px-3.5 py-2.5 rounded-lg rounded-br-sm bg-ink-900 text-white text-sm leading-relaxed whitespace-pre-wrap break-words">
        {renderWithBold(message.content)}
      </div>
    </div>
  );
};

/** A single stop row on a structured stop-list confirmation card, with the
 * allocated duration called out as a badge instead of buried in prose. */
const ConfirmStopRow: React.FC<{
  stop: ConfirmStopItem;
  index: number;
  /** When provided, the row shows a collapsed location toggle + a "Swap" button. */
  onSwap?: () => void;
  /** When provided, the row shows a "Remove" button to drop this stop. */
  onRemove?: () => void;
  disabled?: boolean;
}> = ({ stop, index, onSwap, onRemove, disabled }) => {
  const [showLocation, setShowLocation] = useState(false);
  const mapsUrl = `https://maps.google.com/?q=${encodeURIComponent(stop.address)}`;
  // Drive leg into this stop (from the previous stop, or the route start),
  // shown as a connector so the operator can eyeball how tight the route is.
  const legText =
    stop.driveFromMinutes != null
      ? `${stop.driveFromMinutes} min${stop.driveFromMiles != null ? ` · ${stop.driveFromMiles} mi` : ''} ${
          stop.driveFromLabel === 'start' ? 'from start' : `from ${stop.driveFromLabel}`
        }`
      : null;
  return (
    <>
      {legText && (
        <div className="flex items-center gap-1.5 pl-2 text-2xs text-ink-400">
          <Navigation className="w-2.5 h-2.5 flex-shrink-0 -rotate-90" />
          <span className="truncate">{legText}</span>
        </div>
      )}
    <div className="rounded-lg border border-ink-200 bg-ink-50/60 px-3 py-2.5">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-md bg-ink-900 text-white text-2xs font-bold flex items-center justify-center">
            {index + 1}
          </span>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-ink-800 truncate">{stop.title}</p>
            <p className="text-2xs text-ink-500 mt-0.5">
              {stop.dateLabel} · {stop.timeLabel}
            </p>
          </div>
        </div>
        <span className="flex-shrink-0 rounded-md bg-blue-600 text-white text-2xs font-bold px-2 py-0.5 whitespace-nowrap">
          {stop.durationMinutes} min
        </span>
      </div>

      {stop.badge && (
        <span className="ml-7 mt-1.5 inline-block rounded-md bg-amber-100 text-amber-700 text-2xs font-medium px-2 py-0.5">
          {stop.badge}
        </span>
      )}

      {/* Collapsed by default so the plan stays scannable; each stop can expand
          to reveal its exact location (with a maps link) and swap to another
          option without losing the rest of the plan. */}
      <div className="ml-7 mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
        <button
          type="button"
          onClick={() => setShowLocation((v) => !v)}
          className="inline-flex items-center gap-1 text-2xs font-medium text-ink-500 hover:text-ink-700 transition-colors"
          aria-expanded={showLocation}
        >
          <MapPin className="w-3 h-3" />
          {showLocation ? 'Hide location' : 'Location'}
        </button>
        {onSwap && (
          <button
            type="button"
            onClick={onSwap}
            disabled={disabled}
            className="inline-flex items-center gap-1 text-2xs font-medium text-blue-600 hover:text-blue-700 transition-colors disabled:opacity-50"
          >
            <Shuffle className="w-3 h-3" />
            Swap
          </button>
        )}
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            disabled={disabled}
            className="inline-flex items-center gap-1 text-2xs font-medium text-red-500 hover:text-red-600 transition-colors disabled:opacity-50"
          >
            <X className="w-3 h-3" />
            Remove
          </button>
        )}
      </div>

      {showLocation && (
        <div className="ml-7 mt-1.5 rounded-md border border-ink-200 bg-surface px-2.5 py-2">
          <p className="text-2xs text-ink-600 leading-snug">{stop.address}</p>
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-flex items-center gap-1 text-2xs font-medium text-blue-600 hover:text-blue-700"
          >
            <Navigation className="w-3 h-3" />
            Open in Maps
          </a>
        </div>
      )}
    </div>
    </>
  );
};

const ConfirmActionCard: React.FC<{
  title: string;
  subtitle?: string;
  lines: Array<{ icon?: string; text: string }>;
  stops?: ConfirmStopItem[];
  timeChange?: ConfirmTimeChange;
  changes?: ConfirmChange[];
  outline?: ConfirmOutlineRow[];
  confirmLabel: string;
  tone?: string;
  notify?: { channelLabel: string; defaultOn?: boolean };
  alternatives?: Array<{ id: string; label: string }>;
  onAlternative?: (id: string) => void;
  onSwapStop?: (stop: ConfirmStopItem, index: number) => void;
  onRemoveStop?: (stop: ConfirmStopItem, index: number) => void;
  onConfirm: (notify: boolean) => void;
  onCancel: () => void;
  /** Render as a static transcript record: no buttons, toggles, or actions. */
  readOnly?: boolean;
}> = ({ title, subtitle, lines, stops, timeChange, changes, outline, confirmLabel, tone, notify, alternatives, onAlternative, onSwapStop, onRemoveStop, onConfirm, onCancel, readOnly }) => {
  const danger = tone === 'danger';
  const icons = LINE_ICONS as Record<string, React.ComponentType<{ className?: string }>>;
  const [notifyOn, setNotifyOn] = useState<boolean>(notify?.defaultOn ?? false);
  const totalMinutes = stops?.reduce((sum, s) => sum + (s.durationMinutes || 0), 0) ?? 0;
  return (
    <div
      className={`routy-msg-in rounded-lg bg-surface border shadow-sm overflow-hidden ml-2 sm:ml-10 ${
        danger ? 'border-red-200' : 'border-ink-200'
      }`}
    >
      <div
        className={`px-4 py-2.5 border-b flex items-center gap-2 ${
          danger ? 'bg-red-50 border-red-100' : 'bg-ink-50 border-ink-200'
        }`}
      >
        {danger ? (
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
        ) : (
          <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <span className={`text-sm font-semibold block ${danger ? 'text-red-900' : 'text-ink-900'}`}>{title}</span>
          {subtitle && (
            <span className={`text-xs font-medium block truncate ${danger ? 'text-red-700/80' : 'text-ink-500'}`}>
              {subtitle}
            </span>
          )}
        </div>
        {stops && stops.length > 1 && (
          <span className="text-2xs font-semibold text-blue-700 bg-blue-100 rounded-md px-2 py-0.5 whitespace-nowrap">
            ~{formatDurationMinutes(totalMinutes)} total
          </span>
        )}
      </div>
      {timeChange && (
        <div className="px-4 pt-3 pb-1 space-y-1.5">
          <div className="rounded-lg border border-ink-200 bg-ink-50 px-3 py-2.5 flex items-center gap-2.5">
            <ArrowUp className="w-3.5 h-3.5 flex-shrink-0 text-ink-400" />
            <div className="min-w-0">
              <p className="text-2xs font-semibold uppercase tracking-wide text-ink-400">Currently</p>
              <p className="text-xs text-ink-500 line-through decoration-ink-300">
                {timeChange.oldDateLabel} · {timeChange.oldTimeLabel}
              </p>
            </div>
          </div>
          <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 flex items-center gap-2.5">
            <ArrowDown className="w-3.5 h-3.5 flex-shrink-0 text-blue-600" />
            <div className="min-w-0">
              <p className="text-2xs font-semibold uppercase tracking-wide text-blue-500">New time</p>
              <p className="text-xs font-semibold text-blue-900">
                {timeChange.newDateLabel} · {timeChange.newTimeLabel}
              </p>
            </div>
          </div>
        </div>
      )}
      {stops && stops.length > 0 && (
        <div className="px-4 pt-3 pb-3 space-y-2">
          {stops.map((stop, i) => (
            <ConfirmStopRow
              key={i}
              stop={stop}
              index={i}
              onSwap={onSwapStop ? () => onSwapStop(stop, i) : undefined}
              onRemove={onRemoveStop && stops.length > 1 ? () => onRemoveStop(stop, i) : undefined}
            />
          ))}
        </div>
      )}
      {changes && changes.length > 0 && (
        <div className="mx-4 mt-3 rounded-lg border border-ink-200 divide-y divide-ink-100">
          {changes.map((change, i) => (
            <div key={i} className="px-3 py-2">
              <p className="text-2xs font-semibold uppercase tracking-wide text-ink-400">{change.label}</p>
              <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs">
                <span className="text-ink-500 line-through decoration-ink-300">{change.from}</span>
                <ArrowRight className="w-3 h-3 flex-shrink-0 text-ink-400" />
                <span className="font-semibold text-ink-900">{change.to}</span>
              </p>
            </div>
          ))}
        </div>
      )}
      {lines.length > 0 && (
        <div className="px-4 py-3 space-y-2.5">
          {lines.map((line, i) => {
            const Icon = line.icon ? icons[line.icon] : null;
            return (
              <div key={i} className="flex items-start gap-2 text-xs text-ink-600">
                {Icon && <Icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-ink-400" />}
                <span>{line.text}</span>
              </div>
            );
          })}
        </div>
      )}
      {outline && outline.length > 0 && (
        <div className={`px-4 pb-3 ${lines.length > 0 ? '' : 'pt-3'}`}>
          <div className="max-h-72 overflow-y-auto rounded-lg border border-ink-200 bg-ink-50/60 px-3 py-2 space-y-1.5">
            {outline.map((row, i) => (
              <div key={i} className="flex items-start gap-2" style={{ paddingLeft: row.depth * 14 }}>
                <div className="min-w-0 flex-1">
                  <p className={`text-xs ${row.tag === 'Removed' ? 'text-ink-400 line-through' : 'text-ink-800'}`}>{row.text}</p>
                  {row.detail && <p className="text-2xs text-ink-400 leading-snug">{row.detail}</p>}
                </div>
                {row.tag && (
                  <span className={`flex-shrink-0 rounded px-1.5 py-0.5 text-2xs font-semibold ${OUTLINE_TAG_CLASSES[row.tag]}`}>
                    {row.tag}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {notify && !readOnly && (
        // mt-3 keeps the box off the header divider when the card has no
        // lines/stops section above it (QA 86bavq6cj: it rendered flush
        // against the header, reading as a misaligned double border).
        <div
          className={`mx-4 mb-1 rounded-lg border border-ink-200 bg-ink-50/60 px-3 py-2.5 ${
            lines.length === 0 && !(stops && stops.length > 0) && !timeChange && !changes?.length && !outline?.length
              ? 'mt-3'
              : ''
          }`}
        >
          <button
            type="button"
            onClick={() => setNotifyOn((v) => !v)}
            className="w-full flex items-center gap-2.5 text-left"
            role="switch"
            aria-checked={notifyOn}
          >
            <Bell className={`w-3.5 h-3.5 flex-shrink-0 ${notifyOn ? 'text-blue-600' : 'text-ink-400'}`} />
            <span className="flex-1 text-xs font-medium text-ink-700">
              Notify the customer ({notify.channelLabel})?
            </span>
            <span
              className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${
                notifyOn ? 'bg-blue-600' : 'bg-ink-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-surface shadow transition-transform ${
                  notifyOn ? 'translate-x-4' : 'translate-x-0.5'
                }`}
              />
            </span>
          </button>
          <p className="mt-1.5 pl-6 text-2xs text-ink-400">
            {notifyOn ? `A ${notify.channelLabel} message will be sent.` : 'No message will be sent.'}
          </p>
        </div>
      )}

      {alternatives && alternatives.length > 0 && !readOnly && (
        <div className="px-4 pb-2 space-y-1.5">
          <p className="text-2xs font-medium uppercase tracking-wide text-ink-400">Or</p>
          {alternatives.map((alt) => (
            <button
              key={alt.id}
              onClick={() => onAlternative?.(alt.id)}
              className="w-full text-left text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md px-3 py-2 transition-colors"
            >
              {alt.label}
            </button>
          ))}
        </div>
      )}

      {readOnly ? (
        <div className="px-4 pt-1 pb-3">
          <p className="text-2xs text-ink-400">Not applied yet — tell me when you're ready and I'll bring this back up to confirm.</p>
        </div>
      ) : (
        <div className="flex gap-2 px-4 pt-1 pb-3">
          <button
            onClick={onCancel}
            className="flex-1 py-2 text-sm font-medium text-ink-600 bg-ink-100 rounded-lg hover:bg-ink-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(notifyOn)}
            className={`flex-1 py-2 text-sm font-medium text-white rounded-lg active:scale-[0.98] transition-all ${
              danger ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      )}
    </div>
  );
};

/** Map a backend conversation message onto the panel's render model. */
function backendMessageToRoutyMessage(message: RoutyChatMessage): RoutyMessage {
  return {
    id: message.id,
    role: message.role,
    content: message.content,
    artifacts: message.artifacts?.length ? message.artifacts : undefined,
    proposedActions: message.proposedActions?.length ? message.proposedActions : undefined,
  };
}

export default RoutyPanel;
